import type { ClientMsg, ServerMsg, Speed } from "./protocol";

// Close codes the GameRoom sends (mtg/platform/room_runtime.spell); any other close reconnects.
const CLOSE_REPLACED = 4001;
const CLOSE_TOKEN_REJECTED = 4003;
const CLOSE_GAME_GONE = 4004;
const CLOSE_BANNED = 4005;
const CLOSE_IN_ANOTHER_GAME = 4009;
// This client's own closes: after it missed some History, and after the server went quiet.
const CLOSE_TO_RESYNC = 4000;
const CLOSE_SERVER_SILENT = 4002;

const FIRST_RETRY_MS = 500;
const LONGEST_RETRY_MS = 10_000;
// The abandoned-game timer: by then the Room has ended the game.
const GIVE_UP_AFTER_MS = 15 * 60_000;
// Not against idle closes, which a deployed room never saw in an hour: an unanswered Ping is
// how the client notices a dead socket.
const PING_EVERY_MS = 30_000;

const PING: ClientMsg = { kind: "Ping", values: [] };

// The release Worker refuses a socket that doesn't offer it.
const PROTOCOL = "mtg.v1";
// Browsers can't set a socket's headers, so the access token rides in a subprotocol beside
// mtg.v1; the Worker strips it, and the room's 101 echoes only mtg.v1.
const BEARER_PROTOCOL_PREFIX = "mtg.bearer.";

// Who this page's player is, to the server: AuthKit's access token, or dev auth's ?user=.
export type Credential =
  | { kind: "bearer"; token: string }
  | { kind: "dev-user"; user: string };

// What a credential source throws once its player is signed out, so retrying can't help.
export class SignedOut extends Error {}

export type WireStatus =
  | { kind: "connecting" }
  | { kind: "open" }
  | { kind: "reconnecting" }
  | { kind: "banned" }
  | { kind: "replaced" }
  | { kind: "game-gone" }
  | { kind: "in-another-game" }
  | { kind: "signed-out" }
  | { kind: "gave-up" };

export type WireConfig = {
  game: string;
  // Asks the room to connect this player as a spectator, who sits nowhere: `?watch=1`.
  watch?: boolean;
  // Called before every attempt, so a reconnect never offers an expired token; `refresh` after
  // the server closed with 4003, so the token is a new one even if this page thinks the old
  // one is still good.
  fresh_credential: (refresh: boolean) => Promise<Credential>;
  on_message: (msg: ServerMsg) => void;
  on_status: (status: WireStatus) => void;
  // Two intervals with nothing from the server after a Ping is a dead socket.
  ping_every_ms?: number;
  on_round_trip?: (ms: number) => void;
  server_is_busy?: () => boolean;
};

export class Wire {
  private readonly cfg: WireConfig;
  private readonly pingEveryMs: number;
  private ws: WebSocket | null = null;
  // Only until the first open: after that, an answer sent while disconnected could
  // land on a Decision that has moved on, so it is dropped.
  private outbox: ClientMsg[] = [];
  private everOpened = false;
  private retriesSinceOpen = 0;
  private unreachableSince: number | null = null;
  private retryTimer: number | null = null;
  private pingTimer: number | null = null;
  private silenceTimer: number | null = null;
  private pingSentAt: number | null = null;
  private stopped = false;
  private tokenRejected = false;

  constructor(cfg: WireConfig) {
    this.cfg = cfg;
    this.pingEveryMs = cfg.ping_every_ms ?? PING_EVERY_MS;
    cfg.on_status({ kind: "connecting" });
    void this.attempt();
  }

  private async attempt(): Promise<void> {
    this.retryTimer = null;
    let credential: Credential;
    try {
      credential = await this.cfg.fresh_credential(this.tokenRejected);
    } catch (err) {
      if (err instanceof SignedOut) this.stopWith({ kind: "signed-out" });
      else this.reconnectLater();
      return;
    }
    this.tokenRejected = false;
    if (this.stopped) return;
    const ws = new WebSocket(socketUrl(this.cfg.game, credential, this.cfg.watch ?? false), socketProtocols(credential));
    this.ws = ws;
    ws.addEventListener("open", () => { if (ws === this.ws) this.onOpen(ws); });
    ws.addEventListener("close", (e) => { if (ws === this.ws) this.onClose(e.code); });
    ws.addEventListener("message", (e) => { if (ws === this.ws) this.onMessage(e.data); });
  }

  private onOpen(ws: WebSocket): void {
    if (!this.everOpened) {
      for (const msg of this.outbox) ws.send(JSON.stringify(msg));
      this.outbox.length = 0;
      this.everOpened = true;
    }
    this.retriesSinceOpen = 0;
    this.unreachableSince = null;
    this.pingTimer = window.setInterval(() => this.ping(), this.pingEveryMs);
    this.cfg.on_status({ kind: "open" });
    this.ping();
  }

  private onClose(code: number): void {
    this.ws = null;
    this.stopPinging();
    if (this.stopped) return;
    switch (code) {
      case CLOSE_REPLACED:
        this.stopWith({ kind: "replaced" });
        return;
      case CLOSE_BANNED:
        this.stopWith({ kind: "banned" });
        return;
      case CLOSE_GAME_GONE:
        this.stopWith({ kind: "game-gone" });
        return;
      case CLOSE_IN_ANOTHER_GAME:
        this.stopWith({ kind: "in-another-game" });
        return;
      case CLOSE_TOKEN_REJECTED:
        this.tokenRejected = true;
        this.reconnectLater();
        return;
      default:
        this.reconnectLater();
    }
  }

  private onMessage(data: unknown): void {
    this.disarmSilenceTimer();
    let msg: ServerMsg;
    try {
      msg = JSON.parse(String(data)) as ServerMsg;
    } catch (err) {
      console.warn("wire: malformed server msg", err);
      return;
    }
    if (msg.kind === "Pong") {
      if (this.pingSentAt !== null) this.cfg.on_round_trip?.(performance.now() - this.pingSentAt);
      this.pingSentAt = null;
      return;
    }
    this.cfg.on_message(msg);
  }

  // Timed from the first unanswered Ping, so later Pings don't push the deadline back.
  ping(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.pingSentAt ??= performance.now();
    this.send(PING);
    this.silenceTimer ??= window.setTimeout(() => this.dropSilentSocket(), 2 * this.pingEveryMs);
  }

  // A dead socket may not fire its close event for minutes, so this doesn't wait for it.
  private dropSilentSocket(): void {
    this.silenceTimer = null;
    if (this.cfg.server_is_busy?.()) {
      this.silenceTimer = window.setTimeout(() => this.dropSilentSocket(), 2 * this.pingEveryMs);
      return;
    }
    this.ws?.close(CLOSE_SERVER_SILENT, "no Pong");
    this.onClose(CLOSE_SERVER_SILENT);
  }

  private reconnectLater(): void {
    if (this.stopped) return;
    const since = this.unreachableSince ??= Date.now();
    if (Date.now() - since >= GIVE_UP_AFTER_MS) {
      this.stopWith({ kind: "gave-up" });
      return;
    }
    this.cfg.on_status({ kind: this.everOpened ? "reconnecting" : "connecting" });
    this.retryTimer = window.setTimeout(() => void this.attempt(), retryDelayMs(this.retriesSinceOpen));
    this.retriesSinceOpen += 1;
  }

  // A fresh connection is sent the whole History again.
  resync(): void {
    if (!this.ws) return;
    this.cfg.on_status({ kind: "reconnecting" });
    this.ws.close(CLOSE_TO_RESYNC, "missed History");
  }

  stop(): void {
    this.stopped = true;
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.stopPinging();
    const ws = this.ws;
    this.ws = null;
    ws?.close();
  }

  private stopWith(status: WireStatus): void {
    this.stop();
    this.cfg.on_status(status);
  }

  private stopPinging(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
    this.pingSentAt = null;
    this.disarmSilenceTimer();
  }

  private disarmSilenceTimer(): void {
    if (this.silenceTimer !== null) window.clearTimeout(this.silenceTimer);
    this.silenceTimer = null;
  }

  send(msg: ClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else if (!this.everOpened) {
      this.outbox.push(msg);
    }
  }

  ready(): void {
    this.send({ kind: "Ready", values: [] });
  }

  unready(): void {
    this.send({ kind: "Unready", values: [] });
  }

  pickDeck(deckId: string): void {
    this.send({ kind: "PickDeck", values: [deckId] });
  }

  addAiSeat(deckId: string, level: import("./protocol").Level["kind"]): void {
    this.send({ kind: "AddAiSeat", values: [deckId, { kind: level, values: [] }] });
  }

  setStrict(value: boolean): void {
    this.send({ kind: "SetStrict", values: [value] });
  }

  setMulligans(kind: import("./protocol").Mulligans["kind"]): void {
    this.send({ kind: "SetMulligans", values: [{ kind, values: [] }] });
  }

  setClock(setting: import("./protocol").ClockSetting): void {
    this.send({ kind: "SetClock", values: [setting] });
  }

  setGameType(kind: "Standard" | "Commander"): void {
    this.send({ kind: "SetGameType", values: [{ kind, values: [] }] });
  }

  setMaxPlayers(n: number): void {
    this.send({ kind: "SetMaxPlayers", values: [n] });
  }

  // Dev auth only: the room starts from this .scenario board instead of the pregame; "" clears it.
  setScenario(text: string): void {
    this.send({ kind: "SetScenario", values: [text] });
  }

  chooseStartingPlayer(seat: string): void {
    this.send({ kind: "ChooseStartingPlayer", values: [seat] });
  }

  mulligan(): void {
    this.send({ kind: "Mulligan", values: [] });
  }

  keepHand(): void {
    this.send({ kind: "KeepHand", values: [] });
  }

  bottomCards(oids: number[]): void {
    this.send({ kind: "BottomCards", values: [oids] });
  }

  takeAction(action: import("./protocol").Action): void {
    this.send({ kind: "TakeAction", values: [action] });
  }

  // Ctrl held: the server keeps this player's priority after the action until they pass it.
  takeActionHoldingPriority(action: import("./protocol").Action): void {
    this.send({ kind: "TakeActionHoldingPriority", values: [action] });
  }

  endTurn(turn: number): void {
    this.send({ kind: "EndTurn", values: [turn] });
  }

  skipTurn(turn: number): void {
    this.send({ kind: "SkipTurn", values: [turn] });
  }

  resolveStack(): void {
    this.send({ kind: "ResolveStack", values: [] });
  }

  // 732.2: the loop the player has just gone round, taken round this many more times.
  repeatLoop(times: number): void {
    this.send({ kind: "RepeatLoop", values: [times] });
  }

  setStops(stops: import("./protocol").Stops): void {
    this.send({ kind: "SetStops", values: [stops] });
  }

  setAutoPassCards(names: string[]): void {
    this.send({ kind: "SetAutoPassCards", values: [names] });
  }

  setAutoAnswers(answers: import("./protocol").AutoAnswer[]): void {
    this.send({ kind: "SetAutoAnswers", values: [answers] });
  }

  undoLastAction(): void {
    this.send({ kind: "UndoLastAction", values: [] });
  }

  undoToLastPass(): void {
    this.send({ kind: "UndoToLastPass", values: [] });
  }

  choiceAnswer(answer: import("./protocol").ChoiceResult): void {
    this.send({ kind: "ChoiceAnswer", values: [answer] });
  }

  declareAttackers(attacks: import("./protocol").Attack[]): void {
    this.send({ kind: "DeclareAttackers", values: [attacks] });
  }

  declareBlockers(blocks: import("./protocol").Block[]): void {
    this.send({ kind: "DeclareBlockers", values: [blocks] });
  }

  lookAt(look: import("./protocol").Look): void {
    this.send({ kind: "LookAt", values: [look] });
  }

  leaveRoom(): void {
    this.send({ kind: "LeaveRoom", values: [] });
  }

  leaveUncounted(): void {
    this.send({ kind: "LeaveUncounted", values: [] });
  }

  kickUser(targetUserId: string): void {
    this.send({ kind: "KickUser", values: [targetUserId] });
  }

  setExhibition(value: boolean): void {
    this.send({ kind: "SetExhibition", values: [value] });
  }

  setSpeed(speed: Speed["kind"]): void {
    this.send({ kind: "SetSpeed", values: [{ kind: speed, values: [] }] });
  }

  setPaused(paused: boolean): void {
    this.send({ kind: "SetPaused", values: [paused] });
  }

  stepAi(): void {
    this.send({ kind: "StepAi", values: [] });
  }
}

// Relative to the page, so a release's client under /r/<n>/ reaches that release's /r/<n>/ws.
// Never the token: a query string lands in logged URLs.
function socketUrl(game: string, credential: Credential, watch: boolean): string {
  const user = credential.kind === "dev-user" ? `&user=${encodeURIComponent(credential.user)}` : "";
  const url = new URL(`ws?game=${encodeURIComponent(game)}${user}${watch ? "&watch=1" : ""}`, location.href);
  url.protocol = location.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

function socketProtocols(credential: Credential): string[] {
  return credential.kind === "bearer" ? [PROTOCOL, BEARER_PROTOCOL_PREFIX + credential.token] : [PROTOCOL];
}

// The same credential on a plain request: a bearer in Authorization, a dev user in ?user=.
export function authorizedRequest(path: string, credential: Credential, init: RequestInit = {}): [string, RequestInit] {
  if (credential.kind === "dev-user") {
    const separator = path.includes("?") ? "&" : "?";
    return [`${path}${separator}user=${encodeURIComponent(credential.user)}`, init];
  }
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${credential.token}`);
  return [path, { ...init, headers }];
}

// Each delay is drawn from the upper half of its step, so clients that dropped together don't
// all return together.
function retryDelayMs(retriesSinceOpen: number): number {
  const step = Math.min(LONGEST_RETRY_MS, FIRST_RETRY_MS * 2 ** retriesSinceOpen);
  return step / 2 + Math.random() * (step / 2);
}

export function readUrlParams(): { game: string; user: string; watch: boolean; scenario: string } {
  const params = new URLSearchParams(location.search);
  return {
    game: (params.get("game") ?? "").trim(),
    user: (params.get("user") ?? "").trim(),
    watch: params.get("watch") === "1",
    scenario: (params.get("scenario") ?? "").trim(),
  };
}
